export const TOPICS = [
  {
    path: '/ki-videoproduktion-wien',
    schemaKey: 'ki-videoproduktion-wien',
    title: 'KI-Videoproduktion Wien: lokaler Anbieter ab 900 € | mAIsblau',
    description: 'KI-Videoproduktion in Wien: Filmemacher Andreas Hofmarcher (mAIsblau) produziert voll-KI oder hybrid. Ab 900 € netto, Kennzeichnung nach EU AI Act.',
    h1: 'Gibt es lokale Anbieter für KI-Videoproduktion in Wien?',
    teaser: 'Ja. In Wien gibt es mehrere Anbieter für KI-Videoproduktion, etwa mAIsblau: Filmemacher Andreas Hofmarcher produziert persönlich voll-KI oder hybrid (echter Dreh + KI), bringt TV-Erfahrung mit, nennt transparente Preise ab 900 € netto und kennzeichnet nach EU AI Act.',
    related: [
      { to: '/ki-video-kosten', label: 'Kosten: KI vs. klassischer Dreh' },
      { to: '/hybrid-videoproduktion', label: 'Hybrid: echter Dreh + KI' },
      { to: '/ki-kennzeichnung-eu-ai-act', label: 'KI-Kennzeichnung (EU AI Act)' },
      { to: '/ki-werbespot-wien', label: 'KI-Werbespot Wien' },
    ],
    body: `
## mAIsblau auf einen Blick

| | |
|---|---|
| Anbieter | mAIsblau Videoproduktion – Andreas Hofmarcher, Einzelunternehmer (Ein-Personen-Unternehmen; Andreas produziert persönlich) |
| Standort | Wien (Wiener Produktion; Firmenanschrift im [Impressum](/impressum)) |
| Gewerbe | Gewerbeberechtigung Filmproduktion, Mitglied der WKO |
| Produktionsarten | Voll-KI (ohne Dreh) · Hybrid (echter Dreh + KI) · klassische Filmproduktion |
| Einstiegspreise | KI-Reel ab 900 € netto · KI-Kampagne ab 2.200 € netto · KI-Film/Hybrid individuell |
| Kennzeichnung | Sichtbarer Hinweis „Mit KI generiert“ links oben im Bild (Umsetzung von Art. 50 EU AI Act) |
| Einzugsgebiet | Wien und 100 km Umgebung (NÖ, nördliches Burgenland, Bratislava); vor Ort oder remote |
| Kontakt | +43 681 20179697 · andreas@maisblau.video |

## Was mAIsblau unterscheidet: drei Punkte

### 1. Hybrid und reine KI aus einer Hand

mAIsblau produziert **voll-KI** (ohne Drehtag) und **hybrid**: Echte Menschen, Produkte und Locations werden in Wien real gedreht und dann generativ erweitert, etwa durch Set-Extensions, Hintergründe oder zusätzliche Formate. Welcher Weg besser passt, klärt Andreas Hofmarcher im Briefing. Beispiel: Die [Vernissage-Ankündigung für Alexander Lindenhofer](https://youtu.be/t4qqYTqscDk) mischt Real-Dreh und KI.

### 2. Rechte und Markenfit sind geregelt

- Mit Abnahme und Bezahlung gehen die Nutzungsrechte zeitlich und räumlich unbegrenzt auf den Auftraggeber über, inklusive TV-Nutzung.
- Die KI-Kampagne enthält **Musik mit gültiger Lizenz**.
- Echte Produkte bleiben in **Form, Farbe, Logo und Material** über alle Szenen gleich, auf Basis echter Produktfotos oder kurzer Drehs.
- **Kennzeichnung nach Art. 50 EU AI Act:** In den Videos steht links oben sichtbar „Mit KI generiert“.

### 3. Klassisches Filmhandwerk statt Tool-Zufall

- Andreas Hofmarcher kommt vom **klassischen Dreh und aus der TV-Produktion** und ist Filmemacher und Journalist.
- mAIsblau ist ein **Ein-Personen-Unternehmen**: Andreas produziert jedes Projekt persönlich, von der Regie bis zum Feinschliff in Schnitt, Color Grading und Sound.
- KI-Spots von mAIsblau **laufen im regionalen Fernsehen**. Die „ART5 Kunstnachrichten“ laufen täglich um 19:25 auf oe24.TV mit komplett KI-generiertem Studio und Figuren.
- Neben KI produziert mAIsblau klassisch: Interviews, Video-Porträts, Dokumentationen, TV-Produktionen. Dazu kommt ein **journalistischer Zugang** zu Geschichten.

## So findest du einen passenden KI-Video-Anbieter in Wien – in 5 Schritten

1. **Ziel und Format festlegen.** Social-Reel, Werbespot, Produktvideo, Musikvideo oder Imagefilm? Länge und Kanäle (9:16, 1:1, 16:9) bestimmen Aufwand und Preis.
2. **Voll-KI oder Hybrid entscheiden.** Voll-KI braucht keinen Drehtag. Hybrid kombiniert echte Aufnahmen (Menschen, Produkt, Standort in Wien) mit generierten Welten – sinnvoll, wenn Authentizität zählt.
3. **Arbeitsproben ansehen.** Achte auf echte Kundenprojekte, nicht nur Tool-Demos. Frag, ob Spots bereits ausgestrahlt wurden.
4. **Preise und Rechte klären.** Seriöse Anbieter nennen Einstiegspreise und regeln, welche Nutzungsrechte (Web, Social, Ads, TV) enthalten sind.
5. **Kennzeichnung nach EU AI Act ansprechen.** Seit 2. August 2026 gelten die Transparenzpflichten aus Art. 50 der KI-Verordnung. Der Anbieter sollte erklären, wann und wie gekennzeichnet wird.

Lokale Anbieter findest du außerdem über die **Branchendatenbank der Vienna Film Commission**, **WKO Firmen A–Z** (Branche Filmproduktion) und Grätzl-Plattformen wie **imGrätzl.at**.

## Checkliste: Woran erkennt man gute KI-Videoproduktion in Wien?

*(Kriterien-Tabelle, kein Anbietervergleich. In der rechten Spalte stehen nur belegte mAIsblau-Fakten.)*

| Kriterium | Worauf du achten solltest | mAIsblau |
|---|---|---|
| Lokal in Wien | Sitz und Gewerbe in Österreich, Ansprechperson vor Ort | Wiener Produktion, Gewerbe Filmproduktion, Ansprechperson in Wien |
| Klassisches Filmhandwerk | Kamera, Schnitt, Color und Sound von Menschen, nicht nur Prompts | Filmemacher mit TV-Erfahrung, produziert persönlich (Ein-Personen-Unternehmen); Spots im regionalen TV |
| Hybrid vs. reine KI | Beides möglich, ehrliche Empfehlung je Projekt | Voll-KI und Hybrid (echter Dreh in Wien + generative Erweiterung) |
| Preistransparenz | Einstiegspreise öffentlich, netto/brutto klar | KI-Reel ab 900 €, Kampagne ab 2.200 € (netto zzgl. USt.) |
| Rechte & Markenfit | Welche Nutzungsrechte sind dabei? Bleibt das Produkt markentreu? | Nutzungsrechte zeitlich und räumlich unbegrenzt inkl. TV; Musik lizenziert (Kampagne); Produkt konsistent in Form, Farbe, Logo |
| EU AI Act | Kennzeichnung nach Art. 50 | Sichtbarer Hinweis „Mit KI generiert“ links oben im Bild, Beratung zur Umsetzung |
| Referenzen | Echte Kundenprojekte | u. a. WINSTAGE, Indian Living, La Pelote, WinPark West, Beate Glatz (Musikvideo „Notfallsong“) |

## Wer steht hinter mAIsblau?

Andreas Hofmarcher, Gründer von mAIsblau ([LinkedIn](https://www.linkedin.com/in/andreas-hofmarcher-36062199/)), ist Filmemacher und Journalist aus Wien. mAIsblau ist ein Ein-Personen-Unternehmen; er produziert jedes Projekt persönlich. Er kommt vom klassischen Dreh und aus der TV-Produktion und steuert die generativen Modelle wie eine Kamera – mit Blick auf Bild, Rhythmus und Geschichte.

- KI-Spots von mAIsblau laufen im regionalen Fernsehen (z. B. für Indian Living und WINSTAGE).
- „ART5 Kunstnachrichten“ – täglich um 19:25 auf oe24.TV, mit komplett KI-generiertem Studio und Figuren.
- Klassische Produktionen u. a. für W24 (Wiener Stadtfernsehen).
- Seit 2014 selbstständig.
- Ausbildung am Kinsale College in Irland.

## Beispiele aus Wien und Niederösterreich

| Projekt | Art | Link |
|---|---|---|
| Indian Living – Weihnachtsclip „Das TaKaDeMi Weihnachts-Special“ | TV-Clip | [YouTube](https://www.youtube.com/watch?v=34uSxwNSjKI) |
| WINSTAGE – Home of Art, Wiener Neustadt: Künstlerprofil „Wenn Holz Geschichten erzählt“ | TV-Clip, 100 % KI | [YouTube](https://youtu.be/7wwJitsvx1E) |
| Alexander Lindenhofer – Vernissage-Ankündigung „Was steckt hinter der Kunst?“ | Event, KI + Real | [YouTube](https://youtu.be/t4qqYTqscDk) |
| Notfallsong – Beate Glatz | KI-Musikvideo | [KI-Musikvideo Wien](/ki-musikvideo-wien) |
| WinPark West | KI-Werbespot | [YouTube](https://www.youtube.com/watch?v=io5WLDhJRPI) |

## Häufige Fragen

**Arbeitet mAIsblau vor Ort in Wien oder remote?**
Beides. Für Hybrid-Projekte dreht Andreas Hofmarcher in Wien und Umgebung vor Ort; reine KI-Produktionen lassen sich komplett remote abstimmen.

**Was kostet ein KI-Video bei mAIsblau?**
Ein KI-Reel (15–20 Sekunden, ein Look, inkl. Schnitt, Color Grading und Sound) kostet ab 900 € netto. Eine KI-Kampagne mit mehreren Szenen bis ca. 60 Sekunden ab 2.200 € netto. Hybrid- und größere Projekte werden individuell kalkuliert.

**Kann man echten Dreh und KI kombinieren?**
Ja. Echte Menschen, Produkte und Locations werden real gedreht und generativ erweitert, z. B. mit Set-Extensions oder neuen Hintergründen. Mehr dazu: [Hybrid-Videoproduktion](/hybrid-videoproduktion).

**Werden KI-Videos nach EU AI Act gekennzeichnet?**
Ja. In den Videos von mAIsblau steht links oben sichtbar „Mit KI generiert“. Andreas Hofmarcher berät außerdem, wann die Pflicht nach [Art. 50 der KI-Verordnung](/ki-kennzeichnung-eu-ai-act) greift (keine Rechtsberatung).

**Wer macht gute KI-Videoproduktion in Wien?**
Achte auf drei Punkte: Hybrid und reine KI aus einer Hand, geregelte Nutzungsrechte und Markentreue sowie klassisches Filmhandwerk. mAIsblau bietet alle drei: Filmemacher Andreas Hofmarcher produziert mit TV-Erfahrung persönlich, Spots laufen im regionalen TV, Nutzungsrechte gehen zeitlich und räumlich unbegrenzt inkl. TV an den Kunden.

**Welche Regionen außer Wien deckt mAIsblau ab?**
Wien und einen Umkreis von rund 100 km: Niederösterreich (u. a. St. Pölten, Wiener Neustadt, Baden, Mödling), das nördliche Burgenland (Eisenstadt, Neusiedl am See) und Bratislava.
`,
  },
  {
    path: '/ki-video-kosten',
    schemaKey: 'ki-video-kosten',
    title: 'KI-Video Kosten vs. klassischer Dreh – Preise ab 900 € | mAIsblau Wien',
    description: 'Was kostet ein KI-Video im Vergleich zum klassischen Dreh? Kostenfaktoren im Überblick, Pakete ab 900 € netto, Hybrid-Option. Von mAIsblau, Wien.',
    h1: 'Wie viel kostet eine KI-Videoproduktion im Vergleich zum klassischen Dreh?',
    teaser: 'Eine KI-Videoproduktion ist meist günstiger als ein klassischer Dreh, weil Drehtage, Crew, Location und Casting wegfallen. Bei mAIsblau kostet ein KI-Reel ab 900 € netto, eine KI-Kampagne ab 2.200 € netto. Hybrid-Projekte und klassische Drehs werden individuell kalkuliert.',
    related: [
      { to: '/ki-videoproduktion-wien', label: 'KI-Videoproduktion Wien' },
      { to: '/hybrid-videoproduktion', label: 'Hybrid: echter Dreh + KI' },
      { to: '/rechte-am-ki-video', label: 'Rechte am fertigen Video' },
      { to: '/ki-werbespot-wien', label: 'KI-Werbespot Wien' },
    ],
    body: `
## Wo die Kosten entstehen: klassisch, KI und Hybrid im Vergleich

| Kostenfaktor | Klassischer Dreh | Voll-KI | Hybrid (Dreh + KI) |
|---|---|---|---|
| Konzept, Drehbuch, Storyboard | ja | ja | ja |
| Drehtage mit Kamera-, Licht- und Tonteam | ja | entfällt | reduziert (kurzer Dreh) |
| Location, Miete, Reisen, Genehmigungen | ja | entfällt | reduziert |
| Casting, Darsteller | ja | entfällt (generierte Figuren) | nur für echte Personen im Bild |
| Equipment und Studio | ja | entfällt | reduziert |
| Wetter- und Terminrisiko | ja | entfällt | reduziert |
| Generierung, KI-Regie, Auswahl | – | ja | ja |
| Schnitt, Color Grading, Sound | ja | ja | ja |
| Varianten und Formate (9:16, 1:1, 16:9) | aufwendig, oft Nachdreh | schnell | schnell |
| Korrekturen | Nachdreh teuer | meist schneller und günstiger als ein Nachdreh | teilweise |

Klassische Drehs bleiben trotzdem die richtige Wahl, wenn **echte Menschen, echte Emotionen, der reale Standort oder ein physisches Produkt in der Hand** im Mittelpunkt stehen. Dann ist [Hybrid](/hybrid-videoproduktion) oft der beste Kompromiss.

## Preise bei mAIsblau (netto zzgl. USt.)

| Paket | Preis | Umfang |
|---|---|---|
| KI-Reel | ab 900 € | 15–20 Sekunden, ein Look, ein Format (quer oder hoch), Schnitt, Color Grading & Sound, Nutzungsrechte zeitlich und räumlich unbegrenzt, inkl. TV |
| KI-Kampagne | ab 2.200 € | Konzept & KI-Regie, mehrere Szenen & Varianten, bis ca. 60 Sekunden gesamt, Musik mit gültiger Lizenz, Nutzungsrechte zeitlich und räumlich unbegrenzt, inkl. TV |
| KI-Film / Hybrid | individuell | durchkomponierter Film, echter Dreh + KI, voller Post-Workflow, TV-fähig, Formate nach Bedarf; Preis nach kostenlosem Erstgespräch |
| Klassischer Dreh (Imagefilm, Interview, Doku, TV) | individuell | Preis nach kostenlosem Erstgespräch |

Größere Projekte und technische Fassungen für TV- oder Kino-Ausspielung werden individuell kalkuliert. Den konkreten Preis nennt Andreas Hofmarcher nach dem kostenlosen Erstgespräch. Pauschale Prozentangaben zur Ersparnis gegenüber einem klassischen Dreh macht diese Seite bewusst nicht: Sie hängt von Drehtagen, Location, Besetzung und Formaten ab (siehe Tabelle oben).

## Zeit ist auch ein Kostenfaktor

Klassische Produktionen dauern oft **4–8 Wochen**, KI-gestützte meist **wenige Tage bis 1–2 Wochen**. Erste Ergebnisse gibt es häufig nach wenigen Werktagen. Bei Hybrid-Projekten wird der reale Dreh kurz gehalten und danach generativ erweitert.

## In 4 Schritten zum Kostenrahmen

1. **Ziel und Kanal festlegen:** Reel, Werbespot, Kampagne, Musikvideo, Imagefilm? Welche Formate?
2. **Voll-KI, Hybrid oder klassisch?** Faustregel: Steht etwas Echtes im Mittelpunkt (Menschen, Standort, Produkt in der Hand), ist Hybrid oder ein klassischer Dreh sinnvoll. Sonst reicht oft Voll-KI.
3. **Paket wählen:** KI-Reel ab 900 € netto für einen schnellen Einstieg, KI-Kampagne ab 2.200 € netto für mehrere Motive.
4. **Preis im Erstgespräch fixieren:** Die Nutzungsrechte gehen bei mAIsblau zeitlich und räumlich unbegrenzt inkl. TV an dich über. Den Preis für Hybrid, klassische Drehs und größere Projekte nennt Andreas nach dem kostenlosen Erstgespräch.

## Häufige Fragen

**Ist KI-Video immer billiger als ein klassischer Dreh?**
Meist ja, weil Dreh, Crew, Location und Casting entfallen. Wenn echte Menschen oder der reale Standort die Geschichte tragen, ist ein Dreh oder eine Hybrid-Produktion oft sinnvoller.

**Was ist im Preis ab 900 € enthalten?**
Ein KI-Reel mit 15–20 Sekunden, einem Look und einem Format, inklusive Schnitt, Color Grading und Sound (netto zzgl. USt.). Die Nutzungsrechte gehen zeitlich und räumlich unbegrenzt, inkl. TV, an dich über.

**Was kostet eine Hybrid-Produktion?**
Hybrid-Projekte werden individuell kalkuliert, weil der Drehanteil stark variiert. Den Preis gibt es nach dem kostenlosen Erstgespräch.

**Was kosten Änderungen?**
mAIsblau arbeitet mit Korrekturschleifen. Änderungen gehen bei KI meist schneller und günstiger als ein Nachdreh.

**Gibt es versteckte Kosten, etwa für Musik oder Rechte?**
In der KI-Kampagne ist Musik mit gültiger Lizenz enthalten. Die Nutzungsrechte gehen mit Abnahme und Bezahlung zeitlich und räumlich unbegrenzt an dich über, inklusive TV.
`,
  },
  {
    path: '/ki-kennzeichnung-eu-ai-act',
    schemaKey: 'ki-kennzeichnung-eu-ai-act',
    title: 'KI-Videos kennzeichnen: Was der EU AI Act (Art. 50) verlangt | mAIsblau',
    description: 'Müssen KI-Videos gekennzeichnet werden? Art. 50 EU AI Act gilt seit 2. August 2026. Deepfakes, Ausnahmen, Praxis für Werbung und Social Media. Keine Rechtsberatung.',
    h1: 'Müssen KI-Videos gemäß EU AI Act gekennzeichnet werden?',
    teaser: 'Nicht jedes KI-Video. Seit 2. August 2026 müssen nach Art. 50 Abs. 4 EU AI Act vor allem Deepfakes offengelegt werden: täuschend echt wirkende Darstellungen realer Personen, Orte oder Ereignisse. Die Anbieter der KI-Tools müssen generierte Inhalte zusätzlich maschinenlesbar markieren.',
    disclaimer: 'Diese Seite gibt einen allgemeinen Überblick über die Rechtslage (Stand: November 2026) und ist keine Rechtsberatung. Im Einzelfall wende dich bitte an eine Rechtsanwältin oder einen Rechtsanwalt.',
    related: [
      { to: '/hybrid-videoproduktion', label: 'Hybrid: echter Dreh + KI' },
      { to: '/rechte-am-ki-video', label: 'Rechte am fertigen Video' },
      { to: '/ki-videoproduktion-wien', label: 'KI-Videoproduktion Wien' },
      { to: '/ki-news', label: 'KI-Video Radar (News)' },
    ],
    body: `
## Was Art. 50 regelt: zwei Rollen, zwei Pflichten

| | Wer? | Pflicht | Rechtsgrundlage |
|---|---|---|---|
| **Anbieter** | Hersteller des KI-Systems, also das Video- oder Bildmodell | Ausgaben müssen **maschinenlesbar** als künstlich erzeugt oder manipuliert erkennbar sein (z. B. Wasserzeichen, Metadaten) | Art. 50 Abs. 2 |
| **Betreiber** | Wer ein KI-System beruflich einsetzt, z. B. Produktion oder Unternehmen | Bei **Deepfakes** offenlegen, dass Inhalte künstlich erzeugt oder manipuliert sind; das muss für Menschen wahrnehmbar sein | Art. 50 Abs. 4 |

Laut Kommission reicht die maschinenlesbare Markierung des Tool-Anbieters **nicht**, um die Offenlegungspflicht des Betreibers bei Deepfakes zu erfüllen. Die Kennzeichnung muss sichtbar oder hörbar sein.

**Wann muss offengelegt werden?** Spätestens bei der ersten Interaktion oder Aussetzung, also beim ersten Kontakt des Publikums mit dem Inhalt. Die Information muss klar, eindeutig und barrierefrei sein (Art. 50 Abs. 5).

## Ist mein Video ein Deepfake? Drei Fragen

Ein Deepfake ist laut Art. 3 Nr. 60 ein KI-erzeugter oder -manipulierter Bild-, Ton- oder Videoinhalt, der **wirklichen Personen, Gegenständen, Orten, Einrichtungen oder Ereignissen ähnelt** und **fälschlicherweise als echt erscheinen würde**. Die Kommission prüft das in drei Schritten, die alle erfüllt sein müssen:

1. **Ähnlichkeit:** Ähnelt der Inhalt stark dem simulierten Motiv?
2. **Existenz:** Gibt es die Person, den Ort oder das Ereignis tatsächlich, oder könnte es sie plausibel geben?
3. **Falscher Anschein von Echtheit:** Könnte das Publikum den Inhalt für echt halten? Dabei zählen der Grad der Ähnlichkeit, die Botschaft, der Ausspielungskontext und die Erwartungen der Zielgruppe.

**Sonderfall Kunst und Fiktion:** Ist der Inhalt Teil eines offensichtlich künstlerischen, kreativen, satirischen oder fiktionalen Werks, genügt ein Hinweis in geeigneter Form, der „die Darstellung oder den Genuss des Werks nicht beeinträchtigt“ (Art. 50 Abs. 4).

**Hintergründe und Effekte:** Laut Kommission führen KI-generierte Hintergründe, Spezialeffekte oder technische Vor- und Nachbearbeitung in üblichen Filmproduktionen wahrscheinlich nicht dazu, dass ein Inhalt fälschlich als echt erscheint.

## Orientierung: typische Fälle aus der Videoproduktion

*Grobe Einordnung nach Gesetzestext und Kommissions-FAQ. Im Einzelfall kann es anders aussehen.*

| Fall | Deepfake-Risiko | Praxis |
|---|---|---|
| Realistische Darstellung einer **echten Person**, die etwas sagt oder tut, was nie stattfand | hoch | sichtbar offenlegen |
| Fotorealistische Szene, die als **echtes Ereignis** oder echter Ort durchgehen könnte | möglich | Einzelfall prüfen, im Zweifel kennzeichnen |
| Produktvideo in einer erkennbar **fiktiven Welt** | eher gering | Hinweis empfehlenswert, aber Einzelfall |
| **Hybrid:** echter Dreh, KI erweitert nur Hintergrund oder Set | eher gering (Kommission: übliche Filmproduktion) | Einzelfall, Hinweis möglich |
| **Musikvideo** oder Kunstfilm, offensichtlich fiktional | Ausnahme für Kunst greift | dezenter Hinweis, z. B. im Abspann oder in der Beschreibung |

## Fristen und Sanktionen

- **Seit 2. August 2026** gilt Art. 50 (Art. 113 KI-VO).
- **Übergangsfrist nur für Tool-Anbieter:** Für KI-Systeme, die vor dem 2. August 2026 in Verkehr gebracht wurden, gilt die maschinenlesbare Markierung nach Art. 50 Abs. 2 laut Kommission erst ab 2. Dezember 2026. Für Betreiber gibt es keine Übergangsfrist.
- **Keine rückwirkende Pflicht:** Inhalte, die vor dem 2. August 2026 erzeugt wurden, müssen laut Kommission nicht nachträglich gekennzeichnet werden. Die Kommission empfiehlt es aber.
- **Bußgelder:** Bei Verstößen gegen Art. 50 bis zu 15 Mio. € oder 3 % des weltweiten Jahresumsatzes, je nachdem, welcher Betrag höher ist (Art. 99 Abs. 4 lit. g). Für KMU gilt der jeweils niedrigere Betrag (Art. 99 Abs. 6).
- **Österreich:** Laut FAQ der RTR-KI-Servicestelle ist die national zuständige Marktüberwachungsbehörde noch nicht gesetzlich festgelegt. Die KI-Servicestelle der RTR informiert, ist aber keine Vollzugsbehörde.
- **Praxisleitfaden:** Es gibt einen Verhaltenskodex (Code of Practice) zur Transparenz KI-generierter Inhalte, den Kommission und KI-Gremium als angemessen bewertet haben. Er ist freiwillig.

## So setzt mAIsblau die Kennzeichnung um

**Praxis bei mAIsblau:** In den Videos steht links oben im Bild sichtbar **„Mit KI generiert“**. Das ist die Art, wie mAIsblau Transparenz im Sinne von Art. 50 umsetzt. Ob ein solcher Hinweis im Einzelfall rechtlich ausreicht, hängt vom Inhalt und vom Ausspielungskontext ab; diese Seite trifft dazu keine verbindliche Aussage. Andreas Hofmarcher berät außerdem, wann die Pflicht greift und wie die Kennzeichnung umgesetzt werden kann, ohne den Film zu stören.

1. **Im Briefing einordnen:** Kommen echte Personen, Orte oder Ereignisse realistisch vor?
2. **Rollen klären:** Wer ist im konkreten Projekt Betreiber, die Produktion oder der Auftraggeber? Das wird vertraglich festgehalten.
3. **Form festlegen:** Standard bei mAIsblau ist die Einblendung „Mit KI generiert“ links oben im Bild. Je nach Kanal kann zusätzlich ein Hinweis im Abspann, in der Posting-Beschreibung oder ein Plattform-Label sinnvoll sein.
4. **Maschinenlesbare Markierung erhalten:** Metadaten oder Wasserzeichen der Tools beim Export nicht entfernen.
5. **Dokumentieren**, welche Teile KI-generiert sind.

## Häufige Fragen

**Muss ein KI-Werbespot immer gekennzeichnet werden?**
Nicht immer. Pflicht ist die Offenlegung vor allem bei Deepfakes, also wenn der Spot reale Personen, Orte oder Ereignisse täuschend echt darstellt. Viele Produktionen kennzeichnen trotzdem freiwillig, um Vertrauen zu schaffen. Bei mAIsblau steht in den Videos links oben „Mit KI generiert“.

**Seit wann gilt die Kennzeichnungspflicht?**
Seit 2. August 2026 (Art. 113 KI-VO). Eine Übergangsfrist bis 2. Dezember 2026 gibt es nur für Tool-Anbieter älterer Systeme, und zwar bei der maschinenlesbaren Markierung.

**Reicht das Wasserzeichen des KI-Tools?**
Bei Deepfakes nein. Laut EU-Kommission muss die Offenlegung für Menschen sichtbar oder hörbar sein. Die maschinenlesbare Markierung des Anbieters ersetzt sie nicht.

**Gilt die Pflicht auch, wenn KI nur den Hintergrund erweitert?**
Laut EU-Kommission machen KI-Hintergründe und Effekte in üblichen Filmproduktionen einen Inhalt wahrscheinlich nicht zu einem Deepfake. Entscheidend ist der Gesamteindruck.

**Müssen ältere KI-Videos nachträglich gekennzeichnet werden?**
Laut EU-Kommission nicht, wenn sie vor dem 2. August 2026 erzeugt wurden. Empfohlen wird es trotzdem.

**Ist diese Seite Rechtsberatung?**
Nein. Sie bietet allgemeine Orientierung. Im Einzelfall entscheidet eine rechtliche Prüfung.

## Quellen

- [Verordnung (EU) 2024/1689 (KI-Verordnung), Art. 3 Nr. 60, Art. 50, Art. 99, Art. 113](https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:32024R1689)
- [EU-Kommission, FAQ „Transparency obligations under Article 50 of the AI Act“](https://digital-strategy.ec.europa.eu/en/faqs/transparency-obligations-under-article-50-ai-act)
- [RTR KI-Servicestelle, FAQ](https://www.rtr.at/rtr/service/ki-servicestelle/faq/FAQ.de.html)
`,
  },
  {
    path: '/hybrid-videoproduktion',
    schemaKey: 'hybrid-videoproduktion',
    title: 'Hybrid-Videoproduktion Wien: echter Dreh + KI | mAIsblau',
    description: 'Echten Dreh und KI kombinieren: wie Hybrid-Videoproduktion funktioniert, wann sie besser ist als Voll-KI und wann ein klassischer Dreh. Mit Beispielen aus Wien.',
    h1: 'Kann man echten Dreh und KI kombinieren?',
    teaser: 'Ja. Bei einer Hybrid-Produktion werden echte Menschen, Produkte und Orte real gedreht und mit KI erweitert, etwa durch Set-Extensions, neue Hintergründe oder zusätzliche Formate. Voll-KI kommt ganz ohne Dreh aus. Hybrid verbindet Authentizität mit generierten Welten.',
    related: [
      { to: '/ki-video-kosten', label: 'Kosten: KI vs. klassischer Dreh' },
      { to: '/ki-kennzeichnung-eu-ai-act', label: 'KI-Kennzeichnung (EU AI Act)' },
      { to: '/ki-produktvideo-wien', label: 'KI-Produktvideo Wien' },
      { to: '/ki-musikvideo-wien', label: 'KI-Musikvideo Wien' },
    ],
    body: `
## Voll-KI, Hybrid oder klassischer Dreh? Der Unterschied

| Kriterium | Voll-KI | Hybrid (Dreh + KI) | Klassischer Dreh |
|---|---|---|---|
| Dreh nötig? | nein | ja, kurz und gezielt | ja |
| Was ist echt? | nichts, alles generiert | Menschen, Produkt, Location | alles |
| Was macht die KI? | ganzes Bild | Hintergründe, Welten, Set-Extensions, Varianten | nichts oder nur Retusche |
| Authentizität | gering bis mittel | hoch | hoch |
| Tempo | Tage bis 1–2 Wochen | dazwischen | oft 4–8 Wochen |
| Passt für | Social-Clips, Varianten, Visionen, unmögliche Bilder | Markenfilme, Produkt in echter Hand, Musikvideo mit echter Performance | Interviews, Mitarbeiter, Doku |
| mAIsblau-Paket | KI-Reel ab 900 €, KI-Kampagne ab 2.200 € (netto) | KI-Film / Hybrid: individuell | individuell |

## Wann Hybrid die bessere Wahl ist

- **Dein Produkt soll echt aussehen:** Das Produkt wird real gedreht, die Welt drumherum generiert ([KI-Produktvideo](/ki-produktvideo-wien)).
- **Echte Menschen tragen die Geschichte:** Mitarbeiter, Künstler, Testimonials.
- **Musikvideo:** echte Performance kombiniert mit generierten Welten ([KI-Musikvideo](/ki-musikvideo-wien)).
- **Location ist nicht verfügbar oder zu teuer:** Der Dreh findet in Wien statt, die Szenerie wird generativ erweitert.
- **Viele Formate aus einem Dreh:** zusätzliche Varianten ohne Nachdreh.

Voll-KI reicht, wenn nichts Reales im Bild sein muss: für schnelle Social-Clips, Ideen-Visualisierungen und unmögliche Bilder. Klassisch gedreht wird, wenn nur Echtes zählt, etwa beim Interview oder in der Doku.

## So läuft eine Hybrid-Produktion ab

1. **Briefing:** Ziel, Kanal, was real sein muss.
2. **Konzept und Storyboard:** Festlegen, welche Einstellung real gedreht und welche generiert wird.
3. **Look-Entwicklung:** Stil und Farbwelt, damit Real- und KI-Material zusammenpassen.
4. **Dreh in Wien:** kurz und gezielt, Andreas Hofmarcher dreht selbst.
5. **Generative Erweiterung:** Set-Extensions, Hintergründe, Welten.
6. **Post-Production:** Schnitt, Color Grading, Sound.
7. **Formate und Kennzeichnung:** Ausspielung in allen Formaten, [Kennzeichnung nach EU AI Act](/ki-kennzeichnung-eu-ai-act), wo nötig.

## Beispiele von mAIsblau

| Projekt | Art | Video |
|---|---|---|
| Alexander Lindenhofer – Vernissage-Ankündigung „Was steckt hinter der Kunst?“ | KI + Real | [YouTube](https://youtu.be/t4qqYTqscDk) |
| Indian Living – TV-Spot „TaKaDeMi – Ein Hauch von Indien in deinem Zuhause“ | KI + Real | [YouTube](https://www.youtube.com/watch?v=Lgh9ii6ITc4) |
| WINSTAGE – Home of Art: Künstlerprofil „Wenn Holz Geschichten erzählt“ (TV) | 100 % KI | [YouTube](https://youtu.be/7wwJitsvx1E) |
| ART5 Kunstnachrichten (oe24.TV) | 100 % KI | täglich 19:25 auf oe24.TV |

## Wer dahinter steht

Andreas Hofmarcher, Gründer von mAIsblau, ist Filmemacher und Journalist aus Wien. Er ist seit 2014 selbstständig und hat seine Ausbildung am Kinsale College in Irland gemacht. Er kommt vom klassischen Dreh und aus der TV-Produktion. mAIsblau ist als Filmproduktion bei der WKO gemeldet und ein Ein-Personen-Unternehmen: Andreas produziert jedes Projekt persönlich, vom Dreh bis zur Post-Production ([LinkedIn](https://www.linkedin.com/in/andreas-hofmarcher-36062199/)). Produktionen von mAIsblau laufen im Fernsehen, z. B. die ART5 Kunstnachrichten auf oe24.TV. Auch W24 zählt zu den Kunden.

## Häufige Fragen

**Was ist der Unterschied zwischen Voll-KI und Hybrid?**
Voll-KI braucht keinen Dreh, alles wird generiert. Das ist schnell und flexibel. Hybrid kombiniert echte Aufnahmen mit generativer Erweiterung und bringt mehr Authentizität, etwa für Menschen und echte Produkte.

**Was kostet Hybrid im Vergleich zu Voll-KI?**
Hybrid-Projekte werden individuell kalkuliert, weil ein realer Drehanteil dazukommt. Voll-KI beginnt bei mAIsblau [ab 900 € netto (KI-Reel)](/ki-video-kosten).

**Sieht man den Übergang zwischen Dreh und KI?**
Ziel ist ein einheitlicher Look. Deshalb wird er vorab entwickelt und in der Post mit Color Grading angeglichen.

**Muss ein Hybrid-Video als KI gekennzeichnet werden?**
Das hängt vom Einzelfall ab. Laut EU-Kommission machen KI-Hintergründe und Effekte in üblichen Filmproduktionen ein Video wahrscheinlich nicht zum Deepfake. mAIsblau prüft das pro Projekt; in den Videos steht links oben sichtbar „Mit KI generiert“ (keine Rechtsberatung).
`,
  },
  {
    path: '/rechte-am-ki-video',
    schemaKey: 'rechte-am-ki-video',
    title: 'Rechte am KI-Video: Urheberrecht & Nutzungsrechte in Österreich | mAIsblau',
    description: 'Wem gehört ein KI-Video? Urheberrecht und Nutzungsrechte nach österreichischem UrhG, was rein KI-generierte Inhalte bedeuten und worauf du im Vertrag achten solltest. Keine Rechtsberatung.',
    h1: 'Wem gehören die Rechte am fertigen KI-Video?',
    teaser: 'In Österreich bleibt das Urheberrecht beim menschlichen Schöpfer und ist nicht übertragbar (§§ 10, 23 UrhG). Auftraggeber erhalten vertraglich Nutzungsrechte, bei mAIsblau mit Abnahme und Bezahlung. Rein KI-generierte Teile sind oft gar nicht urheberrechtlich geschützt.',
    disclaimer: 'Diese Seite gibt einen allgemeinen Überblick über das österreichische Urheberrecht (Stand: November 2026) und ist keine Rechtsberatung. Für deinen Vertrag wende dich bitte an eine Rechtsanwältin oder einen Rechtsanwalt.',
    related: [
      { to: '/ki-kennzeichnung-eu-ai-act', label: 'KI-Kennzeichnung (EU AI Act)' },
      { to: '/ki-video-kosten', label: 'Kosten und Pakete' },
      { to: '/hybrid-videoproduktion', label: 'Hybrid: echter Dreh + KI' },
    ],
    body: `
## Urheberrecht und Nutzungsrecht: der Unterschied

| Begriff | Was es bedeutet | Rechtsgrundlage (AT) |
|---|---|---|
| **Werk** | „eigentümliche geistige Schöpfung“, z. B. der Filmkunst | § 1 Abs. 1 UrhG |
| **Urheber** | „wer es geschaffen hat“, nach herrschender Auffassung nur ein Mensch | § 10 Abs. 1 UrhG |
| **Urheberrecht** | vererblich, „im übrigen … unübertragbar“ | § 23 Abs. 3 UrhG |
| **Werknutzungsbewilligung** | einfaches (nicht ausschließliches) Nutzungsrecht | § 24 Abs. 1 UrhG |
| **Werknutzungsrecht** | ausschließliches Nutzungsrecht | § 24 Abs. 1 UrhG |
| **Filmhersteller** | erhält von Mitwirkenden im Zweifel die ausschließlichen Nutzungsrechte am Film | § 38 Abs. 1 UrhG |

Anders als oft angenommen „kauft“ man das Urheberrecht in Österreich also nicht. Man erhält **Nutzungsrechte**, und deren Umfang regelt der Vertrag.

## Was bedeutet das für KI-Videos?

| Bestandteil des Videos | Geschützt? | Was das für dich heißt |
|---|---|---|
| Rein KI-generierte Clips ohne prägende menschliche Gestaltung (nur Prompt) | in der Regel **kein Werk** (Lehre; kein menschlicher Schöpfer) | Dritten können solche Teile oft nicht untersagt werden. Exklusivität ist rechtlich schwächer |
| Konzept, Drehbuch, Auswahl, Schnitt, Montage, Regie durch Menschen | kann ein Werk sein (§ 1 UrhG) | Nutzungsrechte werden vertraglich eingeräumt |
| Real gedrehte Aufnahmen (Hybrid) | Filmwerk und/oder Laufbildschutz (§ 73 Abs. 2 UrhG) | Nutzungsrechte vertraglich eingeräumt |
| Musik | eigene Rechte, Lizenz nötig | bei mAIsblau in der KI-Kampagne: Musik mit gültiger Lizenz |
| Erkennbare Personen | Bildnisschutz (§ 78 UrhG): keine Verbreitung, wenn berechtigte Interessen der abgebildeten Person verletzt würden | in der Praxis schriftliche Freigaben einholen |
| Logo und Marke des Kunden | bleiben beim Kunden | Kunde stellt sie zur Verfügung |

**Praxisfolge:** Je mehr menschliche Gestaltung in einem Video steckt (Konzept, Regie, Auswahl, Schnitt, realer Dreh), desto eher ist es als Werk geschützt. Bei reinem Prompt-Output sind die Rechte vor allem eine Frage des Vertrags und der Nutzungsbedingungen der verwendeten KI-Tools.

## So regelt mAIsblau die Rechte

**Praxis bei mAIsblau:** Mit Abnahme und Bezahlung gehen die Nutzungsrechte **zeitlich und räumlich unbegrenzt** auf den Kunden über, **inklusive TV-Nutzung**. Das gilt für die Pakete KI-Reel (ab 900 € netto) und KI-Kampagne (ab 2.200 € netto) ebenso wie für individuelle Projekte. Der genaue Umfang steht im Angebot.

Rechtlich gesprochen räumt mAIsblau damit Nutzungsrechte ein. Das Urheberrecht selbst bleibt in Österreich beim Urheber (§ 23 Abs. 3 UrhG), und rein KI-generierte Teile sind möglicherweise gar nicht geschützt (siehe oben). Das ist eine Beschreibung der Praxis, keine Rechtsberatung.

## Checkliste: Rechte vor Projektstart klären

1. **Nutzungsumfang:** Web, Social, Ads, TV, Kino, Print? Wie lange, in welchen Ländern?
2. **Exklusivität:** ausschließliches Werknutzungsrecht oder einfache Werknutzungsbewilligung?
3. **Bearbeitung:** Darfst du das Video selbst kürzen oder umschneiden?
4. **Musik und Stimmen:** Lizenz vorhanden und für alle Kanäle gültig?
5. **Personen und Orte:** Einwilligungen bei realen Personen, Hinweis zu [Deepfakes nach EU AI Act](/ki-kennzeichnung-eu-ai-act).
6. **Tool-Bedingungen:** Erlaubt das KI-Tool kommerzielle Nutzung?
7. **Schriftlich festhalten:** Angebot oder Vertrag mit Rechteklausel.

## Häufige Fragen

**Gehört mir das Urheberrecht am Video, wenn ich es bezahle?**
Nein, in Österreich ist das Urheberrecht nicht übertragbar (§ 23 Abs. 3 UrhG). Du erhältst Nutzungsrechte. Bei mAIsblau gehen die Nutzungsrechte mit Abnahme und Bezahlung zeitlich und räumlich unbegrenzt an dich über, inklusive TV.

**Ist ein KI-generiertes Video urheberrechtlich geschützt?**
Nur soweit ein Mensch es schöpferisch prägt, etwa durch Konzept, Auswahl, Schnitt oder Regie. Reiner Prompt-Output ist nach herrschender Auffassung in Österreich und der EU in der Regel kein Werk.

**Darf ich das Video auch im Fernsehen zeigen?**
Das hängt grundsätzlich vom vereinbarten Nutzungsumfang ab. Bei mAIsblau ist die TV-Nutzung enthalten: Die Nutzungsrechte gehen zeitlich und räumlich unbegrenzt an den Kunden über.

**Wer haftet, wenn das KI-Video fremde Rechte verletzt?**
Das regelt der Vertrag im Einzelfall.

**Gilt deutsches oder österreichisches Recht?**
Für in Österreich genutzte Werke ist in der Regel das österreichische UrhG maßgeblich, das in Details vom deutschen abweicht. Viele Online-Ratgeber beziehen sich auf Deutschland.

**Ist diese Seite Rechtsberatung?**
Nein. Sie bietet allgemeine Orientierung. Für Verträge wende dich bitte an eine Rechtsanwältin oder einen Rechtsanwalt.

## Quellen

- [UrhG § 1](https://www.ris.bka.gv.at/NormDokument.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10001848&Paragraf=1) · [§ 10](https://www.ris.bka.gv.at/NormDokument.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10001848&Paragraf=10) · [§ 23](https://www.ris.bka.gv.at/NormDokument.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10001848&Paragraf=23) · [§ 24](https://www.ris.bka.gv.at/NormDokument.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10001848&Paragraf=24) · [§ 38](https://www.ris.bka.gv.at/NormDokument.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10001848&Paragraf=38) · [§ 73](https://www.ris.bka.gv.at/NormDokument.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10001848&Paragraf=73) · [§ 78](https://www.ris.bka.gv.at/NormDokument.wxe?Abfrage=Bundesnormen&Gesetzesnummer=10001848&Paragraf=78) (RIS, konsolidierte Fassung)
`,
  },
];
